/**
 * ApprovalsBrowser — paged, filterable queue of pending tool calls.
 *
 * Layout:
 *   1. Toolbar: status filter + agent filter + Refresh.
 *   2. Table: tool / agent / requested / expires / risk / status /
 *      actions. Row click opens the right-side drawer.
 *   3. Drawer (`ApprovalDrawer`): full row, JSON args, approve / deny
 *      buttons with optional note. Buttons hide when the viewer's role
 *      doesn't satisfy `Approval.required_role` (matches the backend
 *      gate, which is the source of truth).
 *
 * We auto-refresh every 15s when the page is visible. That's the
 * cheapest possible "approval pinged me" signal until L2 ships an SSE
 * channel; throttled enough to not hammer the gateway, snappy enough
 * that an operator who keeps the tab open sees their teammate's
 * decision land within a card-flip.
 */

import { Loader2, RefreshCw, ShieldAlert, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import {
  APPROVAL_STATUSES,
  type AgentListResponse,
  type AgentSummary,
  type ApprovalListResponse,
  type ApprovalStatus,
  type ApprovalSummary,
  type ToolRiskTier,
} from "@/lib/types";
import { cn } from "@/lib/utils";

import { ApprovalDrawer } from "./approval-drawer";

const REFRESH_INTERVAL_MS = 15_000;
const PAGE_SIZE = 50;
// Sentinel value used in the Select dropdowns. Radix Select rejects
// empty-string values, so we pick a string that can't collide with a
// real status / UUID and translate it back to "no filter" before the
// API call.
const ALL = "__all__";

interface ApprovalsBrowserProps {
  /** ``ctx.role`` from the resolved tenant — drives whether resolve
   *  buttons are rendered. Backend still enforces; this is purely UX. */
  viewerRole: string;
  /** Used to render "decided by you" instead of an opaque uuid. */
  viewerUserId: string;
}

export function ApprovalsBrowser({ viewerRole, viewerUserId }: ApprovalsBrowserProps) {
  const t = useT();
  const [items, setItems] = useState<ApprovalSummary[] | null>(null);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ApprovalStatus | typeof ALL>("pending");
  const [agentFilter, setAgentFilter] = useState<string | typeof ALL>(ALL);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Latest filters live in a ref so the polling effect doesn't have to
  // re-subscribe on every keystroke (and so the in-flight refresh
  // always reads the *current* filter, not the one captured when the
  // interval was set up).
  const filtersRef = useRef({ statusFilter, agentFilter });
  filtersRef.current = { statusFilter, agentFilter };

  const refresh = useCallback(async (silent = false): Promise<void> => {
    if (!silent) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      const { statusFilter: s, agentFilter: a } = filtersRef.current;
      if (s !== ALL) params.set("status", s);
      if (a !== ALL) params.set("agent_id", a);
      const data = await api<ApprovalListResponse>(`/approvals?${params.toString()}`);
      setItems(data.items);
      setError(null);
    } catch (err) {
      // Don't blow away the table on a flaky background refresh —
      // surface the error inline but keep the last good rows.
      const message = err instanceof Error ? err.message : String(err);
      if (silent) {
        // eslint-disable-next-line no-console
        console.warn("approvals refresh failed", message);
      } else {
        setError(message);
      }
    } finally {
      if (!silent) setRefreshing(false);
      setLoading(false);
    }
  }, []);

  // Initial load + agent index for the filter dropdown. Agents come
  // from the same /v1/agents endpoint AgentsBrowser uses; we only need
  // id + name, so the response is small enough to fetch all-at-once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api<AgentListResponse>("/agents?include_archived=true&limit=200");
        if (!cancelled) setAgents(list.items);
      } catch {
        // Non-fatal — the filter degrades to ID-only display.
      }
    })();
    void refresh();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // Background polling loop. Pauses when the tab is hidden so we don't
  // burn quota on a backgrounded console; the visibilitychange handler
  // kicks an immediate refresh on resume so the operator sees fresh
  // rows the moment they switch back.
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | null = null;
    const start = (): void => {
      if (id !== null) return;
      id = setInterval(() => {
        void refresh(true);
      }, REFRESH_INTERVAL_MS);
    };
    const stop = (): void => {
      if (id === null) return;
      clearInterval(id);
      id = null;
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        void refresh(true);
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  // Re-fetch when filters change. Two refresh paths (filter-driven vs
  // visibility-driven) both go through the same `refresh()` so there's
  // exactly one HTTP shape to debug. `refresh` reads filters via
  // `filtersRef.current` so it doesn't appear in its closure — but we
  // *want* the effect to re-run on filter change, so list them in deps
  // explicitly.
  // biome-ignore lint/correctness/useExhaustiveDependencies: filters drive intentional re-fetch via ref read inside refresh()
  useEffect(() => {
    void refresh();
  }, [refresh, statusFilter, agentFilter]);

  const agentNameById = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const a of agents) out[a.id] = a.name;
    return out;
  }, [agents]);

  const selected = useMemo(
    () => (selectedId ? (items?.find((row) => row.id === selectedId) ?? null) : null),
    [items, selectedId],
  );

  const onResolved = useCallback(
    (updated: ApprovalSummary) => {
      // Optimistic-style: patch the row locally and trigger a silent
      // refresh in the background. Local patch keeps the UI snappy;
      // the refresh covers the case where a concurrent decision
      // landed elsewhere (which the API would have surfaced as 409
      // and `ApprovalDrawer` toasted).
      setItems((current) =>
        current ? current.map((row) => (row.id === updated.id ? updated : row)) : current,
      );
      void refresh(true);
    },
    [refresh],
  );

  return (
    <>
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card/40 px-3 py-2.5">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t("approvals.filter.status")}
            </span>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as ApprovalStatus | typeof ALL)}
            >
              <SelectTrigger className="h-8 w-[160px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("approvals.filter.status.all")}</SelectItem>
                {APPROVAL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`approvals.status.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t("approvals.filter.agent")}
            </span>
            <Select value={agentFilter} onValueChange={(v) => setAgentFilter(v)}>
              <SelectTrigger className="h-8 w-[200px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("approvals.filter.agent.all")}</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => {
                void refresh();
              }}
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              {t("common.refresh")}
            </Button>
          </div>
        </div>

        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        {loading && items === null ? (
          <ApprovalsSkeleton />
        ) : items && items.length > 0 ? (
          <div className="rounded-xl border border-border bg-card/40 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("approvals.col.tool")}</TableHead>
                  <TableHead>{t("approvals.col.agent")}</TableHead>
                  <TableHead>{t("approvals.col.risk")}</TableHead>
                  <TableHead>{t("approvals.col.requested")}</TableHead>
                  <TableHead>{t("approvals.col.expires")}</TableHead>
                  <TableHead>{t("approvals.col.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow
                    key={row.id}
                    className={cn("cursor-pointer", selectedId === row.id ? "bg-muted/40" : null)}
                    onClick={() => setSelectedId(row.id)}
                  >
                    <TableCell className="font-medium text-foreground">
                      <div className="flex items-center gap-2">
                        {row.tool_kind === "mcp" ? (
                          <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
                        ) : (
                          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        <span className="truncate">{row.tool_name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          {row.tool_kind}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {agentNameById[row.agent_id] ?? row.agent_id.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      <RiskBadge tier={row.risk_tier} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <RelativeTime iso={row.created_at} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <RelativeTime iso={row.expires_at} future />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState>
            <div className="space-y-2">
              <p>{t("approvals.empty")}</p>
              <Link
                to="/agents"
                className="inline-flex text-xs font-semibold text-primary hover:underline"
              >
                {t("approvals.empty.cta")} →
              </Link>
            </div>
          </EmptyState>
        )}
      </div>

      <Sheet open={selected !== null} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent
          side="right"
          className="w-full max-w-[560px] overflow-y-auto p-0 sm:max-w-[560px]"
        >
          {selected ? (
            <ApprovalDrawer
              approval={selected}
              agentName={agentNameById[selected.agent_id] ?? selected.agent_id.slice(0, 8)}
              viewerRole={viewerRole}
              viewerUserId={viewerUserId}
              onResolved={onResolved}
              onClose={() => setSelectedId(null)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

function ApprovalsSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card/40 p-3">
      <div className="space-y-2">
        {["a", "b", "c", "d"].map((slot) => (
          <Skeleton key={slot} className="h-9 w-full" />
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ApprovalStatus }) {
  const t = useT();
  const variant: Record<ApprovalStatus, "default" | "secondary" | "destructive" | "outline"> = {
    pending: "default",
    approved: "secondary",
    denied: "destructive",
    timeout: "outline",
    cancelled: "outline",
  };
  return (
    <Badge variant={variant[status]} className="text-[10px]">
      {t(`approvals.status.${status}`)}
    </Badge>
  );
}

function RiskBadge({ tier }: { tier: ToolRiskTier }) {
  const t = useT();
  const className: Record<ToolRiskTier, string> = {
    safe: "border-border bg-card/60 text-muted-foreground",
    risky: "border-amber-500/40 bg-amber-500/15 text-amber-600 dark:text-amber-400",
    dangerous: "border-destructive/40 bg-destructive/15 text-destructive",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        className[tier],
      )}
    >
      {t(`approvals.risk.${tier}`)}
    </span>
  );
}

function RelativeTime({ iso, future = false }: { iso: string; future?: boolean }) {
  // Tiny inline formatter so we don't pull in a date lib for one cell.
  // For "expires" we want "in 14m" (future-tense); for "requested" we
  // want "3m ago". Anything more elaborate (locale-aware) can land
  // when a second caller needs it.
  const ts = useMemo(() => Date.parse(iso), [iso]);
  if (Number.isNaN(ts)) return <span title={iso}>—</span>;
  const deltaSec = Math.round((ts - Date.now()) / 1000);
  const abs = Math.abs(deltaSec);
  let label: string;
  if (abs < 60) label = `${abs}s`;
  else if (abs < 3600) label = `${Math.round(abs / 60)}m`;
  else if (abs < 86400) label = `${Math.round(abs / 3600)}h`;
  else label = `${Math.round(abs / 86400)}d`;
  const formatted = future ? (deltaSec > 0 ? `in ${label}` : `${label} ago`) : `${label} ago`;
  return <span title={iso}>{formatted}</span>;
}
